import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getAdminMenuCategories,
  getAdminMenuItems,
  getAdminIngredients,
  createMenuCategory,
  updateMenuCategory,
  deleteMenuCategory,
  createMenuItem,
  updateMenuItem,
  deleteMenuItem,
  uploadMenuItemImage,
  createIngredient,
  updateIngredient,
  deleteIngredient,
  seedBeverageInventory,
  resolveMediaUrl,
  formatApiDetail,
} from '../lib/api';
import { ingredientUsesUnits } from '../lib/inventoryUnits';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Upload, Pizza } from 'lucide-react';

function menuItemImageSrc(it) {
  if (!it?.id) return '';
  if (it.image_url) {
    const resolved = resolveMediaUrl(it.image_url);
    if (resolved) return resolved;
  }
  if (it.image_filename) {
    const v = it.image_updated_at ? `?v=${encodeURIComponent(it.image_updated_at)}` : '';
    return `/api/menu/items/${it.id}/image${v}`;
  }
  return '';
}

const emptyItemForm = {
  name: '',
  description: '',
  size_label: '',
  price: '',
  price_mediana: '300',
  price_grande: '330',
  price_familiar: '370',
  category_id: '',
  ingredient_ids: [],
  recipe: [],
  is_named_specialty: false,
  is_pizza: true,
  item_kind: 'pizza',
  prep_minutes: '',
  is_active: true,
};

export default function AdminMenu() {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('all');

  const [itemDialog, setItemDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState(emptyItemForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [existingImageSrc, setExistingImageSrc] = useState('');
  const previewUrlRef = useRef('');
  const fileInputRef = useRef(null);
  const [itemSubmitting, setItemSubmitting] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  const [catDialog, setCatDialog] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState({
    name: '',
    slug: '',
    sort_order: 0,
    is_active: true,
    deduct_at: '',
  });

  const [ingDialog, setIngDialog] = useState(false);
  const [editingIng, setEditingIng] = useState(null);
  const [ingForm, setIngForm] = useState({ name: '', allergens: '', is_active: true });

  const load = useCallback(async () => {
    try {
      const [c, i, ing] = await Promise.all([
        getAdminMenuCategories(),
        getAdminMenuItems(),
        getAdminIngredients(),
      ]);
      setCategories(c);
      setItems(i);
      setIngredients(ing);
    } catch {
      toast.error('Error al cargar el menú');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const clearFilePreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
    setImagePreviewUrl('');
    setImageFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const onImageFileChange = (file) => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
    setImageFile(file || null);
    if (!file) {
      setImagePreviewUrl('');
      return;
    }
    const url = URL.createObjectURL(file);
    previewUrlRef.current = url;
    setImagePreviewUrl(url);
  };

  const handleItemDialogChange = (open) => {
    setItemDialog(open);
    if (!open) {
      setEditingItem(null);
      clearFilePreview();
      setExistingImageSrc('');
    }
  };

  const displayImageSrc = imagePreviewUrl || existingImageSrc;

  const catName = (id) => categories.find((c) => c.id === id)?.name || '—';

  const filteredItems =
    filterCat === 'all' ? items : items.filter((it) => it.category_id === filterCat);

  const addRecipeRow = () => {
    const firstId = ingredients[0]?.id;
    if (!firstId) {
      toast.error('Crea insumos en la pestaña Ingredientes primero');
      return;
    }
    setItemForm((f) => ({
      ...f,
      recipe: [...(f.recipe || []), { ingredient_id: firstId, grams: '' }],
    }));
  };

  const updateRecipeRow = (index, field, value) => {
    setItemForm((f) => {
      const recipe = [...(f.recipe || [])];
      recipe[index] = { ...recipe[index], [field]: value };
      return { ...f, recipe };
    });
  };

  const removeRecipeRow = (index) => {
    setItemForm((f) => ({
      ...f,
      recipe: (f.recipe || []).filter((_, i) => i !== index),
    }));
  };

  const openNewItem = () => {
    setEditingItem(null);
    clearFilePreview();
    setExistingImageSrc('');
    setItemForm({
      ...emptyItemForm,
      category_id: categories[0]?.id || '',
    });
    setItemDialog(true);
  };

  const openEditItem = (it) => {
    const fresh = items.find((row) => row.id === it.id) || it;
    setEditingItem(fresh);
    clearFilePreview();
    setExistingImageSrc(menuItemImageSrc(fresh));
    const recipe =
      (it.recipe || []).length > 0
        ? it.recipe.map((r) => ({
            ingredient_id: r.ingredient_id,
            grams: String(r.grams ?? ''),
          }))
        : (it.ingredient_ids || []).map((iid) => ({ ingredient_id: iid, grams: '' }));
    setItemForm({
      name: it.name,
      description: it.description || '',
      size_label: it.size_label || '',
      price: String(it.price),
      price_mediana: String(
        it.sizes?.find((s) => s.key === 'mediana')?.price ?? it.price ?? 300,
      ),
      price_grande: String(it.sizes?.find((s) => s.key === 'grande')?.price ?? 330),
      price_familiar: String(it.sizes?.find((s) => s.key === 'familiar')?.price ?? 370),
      category_id: it.category_id,
      ingredient_ids: [...(it.ingredient_ids || [])],
      recipe,
      is_named_specialty: !!it.is_named_specialty,
      is_pizza: it.is_pizza !== false && it.item_kind !== 'topping_extra',
      item_kind: it.item_kind || (it.is_pizza ? 'pizza' : ''),
      prep_minutes: it.prep_minutes != null ? String(it.prep_minutes) : '',
      is_active: it.is_active !== false,
    });
    setItemDialog(true);
  };

  const submitItem = async (e) => {
    e.preventDefault();
    setItemSubmitting(true);
    try {
      const recipe = (itemForm.recipe || [])
        .filter((r) => r.ingredient_id && parseFloat(r.grams) > 0)
        .map((r) => ({
          ingredient_id: r.ingredient_id,
          grams: parseFloat(r.grams),
        }));
      const isPizza = itemForm.is_pizza && itemForm.item_kind !== 'topping_extra';
      const payload = {
        name: itemForm.name.trim(),
        description: itemForm.description.trim(),
        size_label: itemForm.size_label.trim(),
        price: isPizza
          ? parseFloat(itemForm.price_mediana || itemForm.price)
          : parseFloat(itemForm.price),
        category_id: itemForm.category_id,
        recipe,
        is_named_specialty: itemForm.is_named_specialty,
        is_pizza: isPizza,
        item_kind: isPizza ? 'pizza' : itemForm.item_kind || '',
        allows_half_specialty: isPizza,
        allows_extra_ingredients: isPizza,
        sizes: isPizza
          ? [
              { key: 'mediana', label: 'Mediana', price: parseFloat(itemForm.price_mediana) || 0 },
              { key: 'grande', label: 'Grande', price: parseFloat(itemForm.price_grande) || 0 },
              { key: 'familiar', label: 'Familiar', price: parseFloat(itemForm.price_familiar) || 0 },
            ]
          : [],
        prep_minutes: itemForm.prep_minutes ? parseInt(itemForm.prep_minutes, 10) : null,
        is_active: itemForm.is_active,
      };
      let id = editingItem?.id;
      if (editingItem) {
        await updateMenuItem(editingItem.id, payload);
        toast.success('Producto actualizado');
      } else {
        const created = await createMenuItem(payload);
        id = created.id;
        toast.success('Producto creado');
      }
      if (imageFile && id) {
        const uploaded = await uploadMenuItemImage(id, imageFile);
        setItems((prev) =>
          prev.map((row) => (row.id === id ? { ...row, ...uploaded } : row)),
        );
        toast.success('Imagen guardada');
        clearFilePreview();
      }
      setItemDialog(false);
      load();
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, 'Error al guardar'));
    } finally {
      setItemSubmitting(false);
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm('¿Eliminar este producto del menú?')) return;
    try {
      await deleteMenuItem(id);
      toast.success('Eliminado');
      load();
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, 'No se pudo eliminar'));
    }
  };

  const uploadInlineImage = async (id, file) => {
    setImageUploading(true);
    try {
      const uploaded = await uploadMenuItemImage(id, file);
      setItems((prev) => prev.map((row) => (row.id === id ? { ...row, ...uploaded } : row)));
      toast.success('Imagen actualizada');
    } catch (err) {
      toast.error(formatApiDetail(err?.response?.data?.detail, 'Error al subir imagen'));
    } finally {
      setImageUploading(false);
    }
  };

  const submitCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCat) {
        await updateMenuCategory(editingCat.id, catForm);
        toast.success('Categoría actualizada');
      } else {
        await createMenuCategory(catForm);
        toast.success('Categoría creada');
      }
      setCatDialog(false);
      setEditingCat(null);
      load();
    } catch {
      toast.error('Error al guardar categoría');
    }
  };

  const submitIngredient = async (e) => {
    e.preventDefault();
    const usesUnits = (ingForm.unit_display || 'kg') === 'pzas';
    const stockVal = Math.max(0, parseFloat(ingForm.stock_kg || '0'));
    const lowVal = Math.max(0, parseFloat(ingForm.low_stock_kg || '0'));
    const payload = {
      name: ingForm.name.trim(),
      allergens: ingForm.allergens.trim(),
      is_active: ingForm.is_active,
      stock_grams: usesUnits ? stockVal : stockVal * 1000,
      low_stock_grams: usesUnits ? lowVal : lowVal * 1000,
      unit_display: ingForm.unit_display || 'kg',
    };
    try {
      if (editingIng) {
        await updateIngredient(editingIng.id, payload);
        toast.success('Ingrediente actualizado');
      } else {
        await createIngredient(payload);
        toast.success('Ingrediente creado');
      }
      setIngDialog(false);
      setEditingIng(null);
      load();
    } catch {
      toast.error('Error al guardar ingrediente');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground animate-pulse">
        Cargando menú…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-menu">
      <div>
        <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
          <Pizza className="w-8 h-8 text-primary" />
          Menú de la pizzería
        </h1>
        <p className="text-muted-foreground mt-1">
          Productos, fotos, ingredientes por pizza y categorías para web y WhatsApp.
        </p>
      </div>

      <Tabs defaultValue="products" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="products">Productos ({items.length})</TabsTrigger>
          <TabsTrigger value="categories">Categorías ({categories.length})</TabsTrigger>
          <TabsTrigger value="ingredients">Ingredientes ({ingredients.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant={filterCat === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterCat('all')}
              >
                Todas
              </Button>
              {categories.map((c) => (
                <Button
                  key={c.id}
                  type="button"
                  size="sm"
                  variant={filterCat === c.id ? 'default' : 'outline'}
                  onClick={() => setFilterCat(c.id)}
                >
                  {c.name}
                </Button>
              ))}
            </div>
            <Dialog open={itemDialog} onOpenChange={handleItemDialogChange}>
              <Button type="button" className="rounded-full gap-2" onClick={openNewItem}>
                <Plus className="w-4 h-4" />
                Nuevo producto
              </Button>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingItem ? 'Editar producto' : 'Nuevo producto'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={submitItem} className="space-y-4">
                  <div>
                    <Label>Nombre *</Label>
                    <Input
                      value={itemForm.name}
                      onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Descripción</Label>
                    <Textarea
                      value={itemForm.description}
                      onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Tamaño (cocina)</Label>
                    <Input
                      value={itemForm.size_label}
                      onChange={(e) => setItemForm({ ...itemForm, size_label: e.target.value })}
                      placeholder="Ej. Mediana / Grande / Familiar"
                      className="mt-1"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <Switch
                      checked={Boolean(itemForm.is_pizza)}
                      onCheckedChange={(v) =>
                        setItemForm({
                          ...itemForm,
                          is_pizza: Boolean(v),
                          item_kind: v ? 'pizza' : '',
                        })
                      }
                    />
                    Es pizza (3 tallas + promos 2x1 / 30%)
                  </label>
                  {itemForm.is_pizza ? (
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label>Mediana *</Label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={itemForm.price_mediana}
                          onChange={(e) => setItemForm({ ...itemForm, price_mediana: e.target.value })}
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Grande *</Label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={itemForm.price_grande}
                          onChange={(e) => setItemForm({ ...itemForm, price_grande: e.target.value })}
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Familiar *</Label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={itemForm.price_familiar}
                          onChange={(e) => setItemForm({ ...itemForm, price_familiar: e.target.value })}
                          required
                          className="mt-1"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Precio (MXN) *</Label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={itemForm.price}
                          onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Min. preparación</Label>
                        <Input
                          type="number"
                          min="1"
                          placeholder="15"
                          value={itemForm.prep_minutes}
                          onChange={(e) => setItemForm({ ...itemForm, prep_minutes: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  )}
                  {itemForm.is_pizza ? (
                    <div>
                      <Label>Min. preparación</Label>
                      <Input
                        type="number"
                        min="1"
                        placeholder="20"
                        value={itemForm.prep_minutes}
                        onChange={(e) => setItemForm({ ...itemForm, prep_minutes: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  ) : null}
                  <div>
                    <Label>Categoría *</Label>
                    <select
                      className="w-full mt-1 border rounded-md h-10 px-3"
                      value={itemForm.category_id}
                      onChange={(e) => setItemForm({ ...itemForm, category_id: e.target.value })}
                      required
                    >
                      <option value="">Selecciona…</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-2">
                      <Label>Receta (cantidad por unidad vendida)</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addRecipeRow}>
                        + Insumo
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 mb-2">
                      Pizzas: gramos de cada insumo. Bebidas empaquetadas: 1 insumo en pzas = 1 botella
                      (receta con cantidad 1). Caja descuenta bebidas al cerrar venta.
                    </p>
                    {(itemForm.recipe || []).length === 0 ? (
                      <p className="text-sm text-muted-foreground border rounded-lg p-3">
                        Sin receta — no descontará inventario. En bebidas, vincula 1 insumo en pzas.
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                        {(itemForm.recipe || []).map((row, idx) => (
                          <div key={idx} className="flex gap-2 items-end">
                            <div className="flex-1 min-w-0">
                              <select
                                className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                                value={row.ingredient_id}
                                onChange={(e) =>
                                  updateRecipeRow(idx, 'ingredient_id', e.target.value)
                                }
                              >
                                {ingredients.map((ing) => (
                                  <option key={ing.id} value={ing.id}>
                                    {ing.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="w-24">
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                placeholder={
                                  ingredientUsesUnits(
                                    ingredients.find((i) => i.id === row.ingredient_id) || {},
                                  )
                                    ? 'pzas'
                                    : 'g'
                                }
                                value={row.grams}
                                onChange={(e) => updateRecipeRow(idx, 'grams', e.target.value)}
                              />
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeRecipeRow(idx)}
                              title="Quitar ingrediente"
                              aria-label="Quitar ingrediente de la receta"
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={itemForm.is_named_specialty}
                        onCheckedChange={(v) => setItemForm({ ...itemForm, is_named_specialty: v })}
                      />
                      <Label>Especialidad con nombre</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={itemForm.is_active}
                        onCheckedChange={(v) => setItemForm({ ...itemForm, is_active: v })}
                      />
                      <Label>Activo</Label>
                    </div>
                  </div>
                  <div>
                    <Label>Foto del producto</Label>
                    <div className="mt-2 flex flex-col items-start gap-2">
                      <div className="w-44 h-44 rounded-lg border-2 border-dashed border-orange-200 bg-orange-50/80 flex items-center justify-center overflow-hidden">
                        {displayImageSrc ? (
                          <img
                            key={displayImageSrc}
                            src={displayImageSrc}
                            alt="Vista previa del producto"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground text-center px-3">
                            Sin imagen — elige un archivo abajo
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {imagePreviewUrl
                          ? 'Vista previa del archivo nuevo (se guardará al pulsar '
                          : existingImageSrc
                            ? 'Imagen actual del producto. Elige otro archivo para reemplazarla al '
                            : 'Elige una imagen para el producto al '}
                        {editingItem ? 'Actualizar' : 'Crear'}).
                      </p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,.jfif"
                      className="mt-2 block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-primary-foreground"
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        onImageFileChange(f);
                      }}
                    />
                    {imagePreviewUrl ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="mt-1 h-8 text-muted-foreground"
                        onClick={clearFilePreview}
                      >
                        Quitar archivo seleccionado
                      </Button>
                    ) : null}
                    <p className="text-xs text-muted-foreground mt-1">
                      Formatos: JPG, PNG, GIF y WEBP.
                    </p>
                  </div>
                  <Button type="submit" className="w-full rounded-full" disabled={itemSubmitting}>
                    {itemSubmitting ? 'Guardando…' : editingItem ? 'Actualizar' : 'Crear'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Foto</TableHead>
                    <TableHead>Producto</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead>Ingredientes</TableHead>
                    <TableHead>Precio</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                        No hay productos. Crea uno con «Nuevo producto».
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredItems.map((it) => (
                      <TableRow key={it.id}>
                        <TableCell>
                          <div className="relative w-14 h-14 rounded-lg overflow-hidden bg-orange-50">
                            {it.image_url ? (
                              <img
                                key={menuItemImageSrc(it)}
                                src={menuItemImageSrc(it)}
                                alt={it.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Pizza className="w-6 h-6 text-orange-300" />
                              </div>
                            )}
                            <label className="absolute inset-0 cursor-pointer opacity-0 hover:opacity-100 hover:bg-black/30 flex items-center justify-center transition-opacity">
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                disabled={imageUploading}
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  e.target.value = '';
                                  if (f) uploadInlineImage(it.id, f);
                                }}
                              />
                              <Upload className="w-4 h-4 text-white" />
                            </label>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">{it.name}</p>
                          <p className="text-xs text-muted-foreground line-clamp-1">{it.description}</p>
                          {it.is_named_specialty && (
                            <Badge variant="secondary" className="mt-1 text-[10px]">
                              Especialidad
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{catName(it.category_id)}</Badge>
                        </TableCell>
                        <TableCell className="max-w-[180px]">
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {(it.ingredients || []).map((i) => i.name).join(', ') || '—'}
                          </p>
                        </TableCell>
                        <TableCell className="font-semibold">${Number(it.price).toFixed(0)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditItem(it)}
                            title={`Editar ${it.name}`}
                            aria-label={`Editar ${it.name}`}
                          >
                            <Pencil className="w-4 h-4" aria-hidden="true" />
                            <span className="hidden xl:inline">Editar</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => removeItem(it.id)}
                            title={`Eliminar ${it.name}`}
                            aria-label={`Eliminar ${it.name}`}
                          >
                            <Trash2 className="w-4 h-4" aria-hidden="true" />
                            <span className="hidden xl:inline">Eliminar</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categories">
          <div className="flex justify-end mb-4">
            <Dialog open={catDialog} onOpenChange={setCatDialog}>
              <DialogTrigger asChild>
                <Button
                  className="rounded-full gap-2"
                  onClick={() => {
                    setEditingCat(null);
                    setCatForm({
                      name: '',
                      slug: '',
                      sort_order: categories.length + 1,
                      is_active: true,
                      deduct_at: '',
                    });
                  }}
                >
                  <Plus className="w-4 h-4" />
                  Nueva categoría
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingCat ? 'Editar categoría' : 'Nueva categoría'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={submitCategory} className="space-y-3">
                  <div>
                    <Label>Nombre</Label>
                    <Input
                      value={catForm.name}
                      onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Orden</Label>
                    <Input
                      type="number"
                      value={catForm.sort_order}
                      onChange={(e) => setCatForm({ ...catForm, sort_order: parseInt(e.target.value, 10) })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Descuento de inventario</Label>
                    <select
                      className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm mt-1"
                      value={catForm.deduct_at}
                      onChange={(e) => setCatForm({ ...catForm, deduct_at: e.target.value })}
                    >
                      <option value="">Automático (bebidas/extras → caja)</option>
                      <option value="kitchen">En cocina (al marcar atendido)</option>
                      <option value="counter">En caja (al marcar listo)</option>
                    </select>
                  </div>
                  <Button type="submit" className="w-full">Guardar</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((c) => (
              <Card key={c.id}>
                <CardContent className="p-4 flex justify-between items-start">
                  <div>
                    <p className="font-semibold">{c.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {items.filter((i) => i.category_id === c.id).length} productos · orden {c.sort_order}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingCat(c);
                        setCatForm({
                          name: c.name,
                          slug: c.slug || '',
                          sort_order: c.sort_order,
                          is_active: c.is_active,
                          deduct_at: c.deduct_at || '',
                        });
                        setCatDialog(true);
                      }}
                      title={`Editar categoría ${c.name}`}
                      aria-label={`Editar categoría ${c.name}`}
                    >
                      <Pencil className="w-4 h-4" aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      title={`Eliminar categoría ${c.name}`}
                      aria-label={`Eliminar categoría ${c.name}`}
                      onClick={async () => {
                        if (!window.confirm(`¿Borrar categoría «${c.name}»?`)) return;
                        try {
                          await deleteMenuCategory(c.id);
                          toast.success('Categoría eliminada');
                          load();
                        } catch (err) {
                          toast.error(formatApiDetail(err.response?.data?.detail, 'No se pudo borrar'));
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="ingredients">
          <div className="flex flex-wrap justify-end gap-2 mb-4">
            <Button
              variant="outline"
              onClick={async () => {
                if (
                  !window.confirm(
                    '¿Crear insumos en piezas y vincular recetas para bebidas sin almacén?',
                  )
                ) {
                  return;
                }
                try {
                  const r = await seedBeverageInventory();
                  toast.success(
                    `Insumos: ${r.ingredients_created || 0}. Vinculados: ${r.items_linked || 0}.`,
                  );
                  load();
                } catch (err) {
                  toast.error(formatApiDetail(err.response?.data?.detail, 'Error'));
                }
              }}
            >
              Sincronizar bebidas con almacén
            </Button>
            <Button variant="outline" asChild>
              <Link to="/admin/almacen">Ver almacén y stock</Link>
            </Button>
            <Dialog open={ingDialog} onOpenChange={setIngDialog}>
              <DialogTrigger asChild>
                <Button
                  className="rounded-full gap-2"
                  onClick={() => {
                    setEditingIng(null);
                    setIngForm({
                      name: '',
                      allergens: '',
                      is_active: true,
                      stock_kg: '0',
                      low_stock_kg: '0',
                      unit_display: 'kg',
                    });
                  }}
                >
                  <Plus className="w-4 h-4" />
                  Nuevo ingrediente
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingIng ? 'Editar ingrediente' : 'Nuevo ingrediente'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={submitIngredient} className="space-y-3">
                  <div>
                    <Label>Nombre</Label>
                    <Input
                      value={ingForm.name}
                      onChange={(e) => setIngForm({ ...ingForm, name: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Alérgenos (opcional)</Label>
                    <Input
                      value={ingForm.allergens}
                      onChange={(e) => setIngForm({ ...ingForm, allergens: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Unidad de stock</Label>
                    <select
                      className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm mt-1"
                      value={ingForm.unit_display || 'kg'}
                      onChange={(e) =>
                        setIngForm({ ...ingForm, unit_display: e.target.value })
                      }
                    >
                      <option value="kg">Kilogramos (kg)</option>
                      <option value="pzas">Piezas (pzas) — bebidas empaquetadas</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>
                        Stock inicial ({ingForm.unit_display === 'pzas' ? 'pzas' : 'kg'})
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        step={ingForm.unit_display === 'pzas' ? '1' : '0.001'}
                        value={ingForm.stock_kg}
                        onChange={(e) => setIngForm({ ...ingForm, stock_kg: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>
                        Alerta bajo ({ingForm.unit_display === 'pzas' ? 'pzas' : 'kg'})
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        step={ingForm.unit_display === 'pzas' ? '1' : '0.001'}
                        value={ingForm.low_stock_kg}
                        onChange={(e) => setIngForm({ ...ingForm, low_stock_kg: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full">Guardar</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="flex flex-wrap gap-2">
            {ingredients.map((ing) => (
              <Badge
                key={ing.id}
                variant="outline"
                className="py-2 px-3 gap-2 cursor-default"
              >
                {ing.name}
                <span className="text-xs text-muted-foreground">
                  ({ingredientUsesUnits(ing) ? 'pzas' : 'kg'})
                </span>
                <button
                  type="button"
                  className="opacity-60 hover:opacity-100"
                  onClick={() => {
                    setEditingIng(ing);
                    const usesUnits = ingredientUsesUnits(ing);
                    setIngForm({
                      name: ing.name,
                      allergens: ing.allergens || '',
                      is_active: ing.is_active,
                      stock_kg: String(
                        usesUnits
                          ? (ing.stock_units ?? ing.stock_grams ?? 0)
                          : (ing.stock_kg ?? (ing.stock_grams || 0) / 1000),
                      ),
                      low_stock_kg: String(
                        usesUnits
                          ? (ing.low_stock_units ?? ing.low_stock_grams ?? 0)
                          : (ing.low_stock_kg ?? (ing.low_stock_grams || 0) / 1000),
                      ),
                      unit_display: ing.unit_display || 'kg',
                    });
                    setIngDialog(true);
                  }}
                  title={`Editar ${ing.name}`}
                  aria-label={`Editar ingrediente ${ing.name}`}
                >
                  <Pencil className="w-3 h-3" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="opacity-60 hover:opacity-100"
                  title={`Eliminar ${ing.name}`}
                  aria-label={`Eliminar ingrediente ${ing.name}`}
                  onClick={async () => {
                    if (!window.confirm(`¿Borrar «${ing.name}»?`)) return;
                    try {
                      await deleteIngredient(ing.id);
                      toast.success('Eliminado');
                      load();
                    } catch {
                      toast.error('No se pudo borrar');
                    }
                  }}
                >
                  <Trash2 className="w-3 h-3 text-destructive" />
                </button>
              </Badge>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
